<template>
    <div>
        <div 
            v-for="(row, idx) in registryRows" 
            :key="row._id"
            class="d-flex gap-3 flex-nowrap align-items-center bg-gray-100 border rounded-lg shadow-sm"
        >
            <CustomFormSchema
                :schema="row.schema"
                :name="row._id"
                :initialData="row.initialData"
                :level="childLevel"
                :disabled="disabled || !row.editActive"
                :rowIndex="idx"
                class="d-flex gap-3 flex-wrap"
                @update="handleRowUpdate(row._id, $event)"
                @mounted="handleRowSchemaMounted(row._id, $event)"
            ></CustomFormSchema>  <!-- :initialData="originalData[row[uniqueIdField]]" -->

            <div class="">
                <div>row index: {{row.index}}</div>
                <div>row ID: {{row._id}}</div>
                <b-button v-if="!noRemove && !disabled" variant="minimal-danger" size="sm" @click="removeRow(row._id)">Remove</b-button>
                <div class="small text-muted">editActive: {{row.editActive}}</div>
                <b-button variant="minimal-primary" size="sm" @click="toggleEditActive(row._id)">{{ row.editActive ? 'Done' : 'Edit' }}</b-button>
            </div>
        
        </div>

        <div v-if="!registryRows.length" class="d-flex align-items-center justify-content-center rounded-lg min-height-100" style="border: 2px dashed var(--gray-300);">
            <div class="text-muted">No Items</div>
        </div>

        <div v-if="showAddButton" class="mt-1">
            <b-button type="button" variant="gray-primary" size="sm" @click="addRow">+ Add Item</b-button>
        </div>

    </div>
</template>

<script>
//import CustomFormSchema from "./CustomFormSchema";
//import cloneDeep from 'lodash/cloneDeep';
import uniqueId from 'lodash/uniqueId';


class CustomFormRow{
    constructor({
        _id,  // the unique ID
        initialData,  // the initial data for this row
        currentData,  // the current actual data for this row
        groupFieldName,  // string name of the group field this is part of
        schema,  // schema for the form, not the schema component
        schemaComp,  // only set this once you have the component
        index,  // current index in the list
        editActive,  // boolean, are we in edit mode for this row?
    }) {
        this._id = _id ?? `row_${uniqueId()}`;
        this.initialData = initialData ?? {};
        this.currentData = currentData ?? {};  // will get updated as we modify things
        this.groupFieldName = groupFieldName || '';
        this.schema = schema;
        this.schemaComp = schemaComp;
        this.passed = undefined;  // set when we updateSelf() - but not reactive
        this.valid = undefined;  // set when we updateSelf() - but not reactive
        this.index = index;
        this.editActive = editActive ?? false;
    }
}

const defaultData = [ 
    {id: 'one'}, {id: 'two'}, {id: 'three'},
]

export default {
    name: 'CustomFormCompGroup',
    components: {
        //CustomFormSchema,
    },
    props: {
        name: {
            type: String,
            required: true,
        },
        value: {  // need value / @input to look like another input
            type: Array,  // value here is an array
            default: () => defaultData,
        },
        children: {
            type: Array, // the SCHEMA FOR CHILDREN - maybe change to call it `schema`?
            default: () => [],
        },
        noAdd: {
            type: Boolean,
            default: false,
        },
        noRemove: {
            type: Boolean,
            default: false,
        },
        level: {  // 0 is root, 1 is first level of nesting - the group field will likely still be 0 -- but its schema / children will be +1
            type: Number,
            required: true,
        },
        disabled: {  // individual inputs should not take json-logic conditionals... just the input wrapper component... I think
            type: Boolean,
            default: false,
        },
        layout: {  // options are 'form' (normal form), 'editForm' (click to edit, but otherwise in display mode), 'grid' ?
            type: String,
            default: 'form',
        },
        // something about layout style?  grid, inputs, etc.
    },
    data: () => ({
        //originalData: {}, // keyed by some unique ID per group item
        //currentData: {}, // keyed by some unique ID per group item
        rowRegistry: {},  // registry of CustomFormRow instances - one for each row
        currentValue: [],  
        uniqueIdField: 'id', // gotta have a unique ID for each row, somehow
        lastEmittedInputPayload: '',  // STRINGIFIED last @input payload for detecting changes
    }),
    computed: {
        registryRows() {
            // ARRAY of all the CustomFormRow instances in the registry
            return Object.values(this.rowRegistry);
        },
        childSchema() {
            return this.children;  // need to modify at all???
        },
        childLevel() {
            return this.level + 1;
        },
        showAddButton() {
            return !this.noAdd && !this.disabled;
        },
    },
    watch: {
        // need to watch `value` to watch for changes from external things (like setData on parent schema)
        // we just check if value changed, and if so, register new rows
        // we have this same logic in the generic input component -- need it both places???
        value: function(val) {
            this.setValue(val);
        },
    },
    created() {
        // get initial data from prop, or wherever original data comes from 
        //const keyedData = {};
        // move this to setValue?  need to also do this on updates to value from external things
        this.setValue(this.value);
        /* this.value.forEach(grp => {
            // TODO - if unique ID not already there, must add one in
            const row = this.createNewRow(grp);
            this.registerRow(row);
        }); */
    },
    methods: {
        setValue(val) {
            // use this in created, or when value changes from external things
            // we check if there is any change between new value (val) and currentValue first
            let newVal = [];  // the new default value, in case we pass in undefined, etc.
            if (val && Array.isArray(val)) {
                newVal = [...val];
            }
            const currentValueStringified = JSON.stringify(this.currentValue);
            const newValStringified = JSON.stringify(newVal);
            if (currentValueStringified !== newValStringified) {
                // then we have a real new value - need to clear currentValue / registry and make new rows
                // TODO - probably better to go through and see if we have any existing rows, using row._id -- if so, just change their value?
                this.currentValue = [];
                this.rowRegistry = {};
                // if newVal is empty, that's fine -- we just clear currentValue and that's it
                newVal.forEach(rowVal => {
                    // gotta make new rows to add unique IDs, etc
                    const row = this.createNewRow(rowVal);
                    this.registerRow(row);
                    // then when they mount we will set new currentValue from that -- but should we not do that one-by-one?
                });
            }            
        },
        registerRow(row) {
            // pass in instance of CustomFormRow, put in registry
            const registryId = row._id;
            this.$set(this.rowRegistry, registryId, row);
        },
        handleRowSchemaMounted(rowId, schemaComp) {
            console.log('in GROUP INPUT - will register row schema component with rowId', rowId, schemaComp, 'with data:', schemaComp.formData);
            // we already have things in registry, but need to attach for schemaComp to each registryRow
            const rowInst = this.rowRegistry[rowId];
            rowInst['schemaComp'] = schemaComp;
            // then when done mounting all rows, call updateSelf() and emitInput() - that will set other things on each rowInst
            const doneMounting = Object.values(this.rowRegistry).every(r => !!r.schemaComp);
            // const rowId = schemaComp.formData[this.uniqueIdField];
            // // TODO 
            // this.$set(this.rowRegistry, rowId, schemaComp);
            // // then updateSelf and emit input -- IF you are done mounting all rows?
            // const rowIdsInData = Object.keys(this.currentData);
            // const rowIdsInRegistry = Object.keys(this.rowRegistry);
            // const doneMounting = rowIdsInData.length === rowIdsInRegistry.length;  // USE SET COMPARISON INSTEAD?
            if (doneMounting) {
                this.updateSelf();
                this.emitInput();
            }
        },
        handleRowUpdate(rowId, updatePayload) {
            const { data, valid, passed } = updatePayload;
            console.log('updatedData from schema row', rowId, data, valid, passed);
            //const rowId = updatedData[this.uniqueIdField];
            // updatedData likely will not have rowId in there.... need to add it
            this.updateSelf();
            this.emitInput();
        },
        updateSelf() {
            // call this when a child updates itself, or when parent wants to update to get latest data
            // this SHOULD already be debouced -- child schemas will do that
            // should we loop over all child schemas and get values?  Or just update the one row that triggered this?
            // since we probably want this to work when called by parent too, we should loop over
            console.log('UPDATING SELF FROM GROUP FIELD', this.name);
            // let passedById = {};
            // let validById = {};
            // let dataById = {};
            //const rowValues = [];
            // WARNING - if you just added a row, might not be mounted yet, so not in rowRegistry
            Object.values(this.rowRegistry).forEach(rowInst => {
                // each schema needs to be updated to trigger all the things
                const rowSchemaComp = rowInst.schemaComp;
                rowSchemaComp.updateSelf();
                rowInst.currentData = rowSchemaComp.formData;  // clone it?
                rowInst.valid = rowSchemaComp.allValid;
                rowInst.passed = rowSchemaComp.allPassed;
                //const rowId = rowInst._id;
                //rowValues.push(rowSchemaComp.formData);
                // rows don't get removed or hidden, right?
            });
            // set currentValue by collecting values as you go?  or by getting currentData at the end after setting currentData for each row....???
            this.currentValue = Object.values(this.rowRegistry).map(r => r.currentData);
            // this.currentData = dataById;  // set entirely new data!  ok?
        },
        emitInput() {
            // emit to parent (part of v-model) -- call this AFTER calling updateSelf
            // even if parent triggers updateSelf() -- because the new value needs to get updated in v-model in parent
            // IMPORTANT - must only do it if value changed -- or else we trigger infinitely
            const currentValue = [...this.currentValue];  // need to copy?
            const currentValueStringified = JSON.stringify(currentValue);
            if (currentValueStringified !== this.lastEmittedInputPayload) {
                // its different... continue to emit and set new last value
                this.lastEmittedInputPayload = currentValueStringified;
                console.log('EMITTING INPUT FROM GROUP FIELD', this.name);
                this.$emit('input', currentValue);
            } else {
                console.log(`from group field ${this.name} - emitInput but no change, so not emitting`);
            }
        },
        createNewRow(initialData) {
            // allow passing in an index to insert between existing rows?
            const index = Object.keys(this.rowRegistry).length;
            const newRowParams = {
                initialData: (initialData ?? {}), 
                groupFieldName: this.name, 
                schema: this.children,
                index: index,
            }
            return new CustomFormRow(newRowParams);
        },
        addRow() {
            // const newRowId = `row_${uniqueId()}`;  // standardize on how to make rowIds...
            // const newRowData = {};
            // newRowData[this.uniqueIdField] = newRowId;  // must add it to the object too!
            const newRow = this.createNewRow();
            console.log('will addRow with this row instance:', newRow);
            this.registerRow(newRow);
            // then let the new schema component register itself and handle from normal registration point?
            // must add data to currentData so our check if all rows are mounted still works 
        },
        removeRow(rowId) {
            console.log('removing rowId', rowId);
            // delete from data, and from registry
            //this.$delete(this.currentData, rowId);
            this.$delete(this.rowRegistry, rowId);
            this.updateSelf();
            this.emitInput();
        },
        toggleEditActive(rowId) {
            // toggle a row to editActive = true
            const row = this.rowRegistry[rowId];
            if (row) {
                const currentEditActive = row.editActive;
                this.$set(row, 'editActive', !currentEditActive);
            }
        },
    },
};
</script>
